import React from 'react';
import { motion, useReducedMotion } from 'motion/react';

/**
 * Fade + slide-up on scroll-into-view. Only animates once, when the element
 * enters the viewport — so above-the-fold content that's already visible on
 * load doesn't lurch. Under prefers-reduced-motion it renders the plain element
 * with no motion (and no hidden initial state, so content is always visible).
 *
 * `as` preserves semantics/layout (e.g. 'section', 'h2') — matters for
 * full-bleed sections and headings.
 */
const Reveal = ({
  as = 'div',
  className = '',
  children,
  delay = 0,
  ...rest
}) => {
  const reduce = useReducedMotion();
  if (reduce) {
    return React.createElement(as, { className, ...rest }, children);
  }
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y: 48 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.7, ease: 'easeOut', delay }}
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...rest}
    >
      {children}
    </MotionTag>
  );
};

export default Reveal;
